/**
 * Product Expanded Row Component
 * Displays detailed product information and quick actions in table expansion
 * Following scheduled-orders expanded row pattern
 */

import {
  Component,
  ChangeDetectionStrategy,
  Input,
  Output,
  EventEmitter,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { ButtonModule } from "primeng/button";
import { TagModule } from "primeng/tag";
import { TooltipModule } from "primeng/tooltip";
import { IProductSummary } from "../../models";

/**
 * Product expanded row action event
 */
export interface IProductExpandedRowAction {
  action: "view" | "edit" | "syncPrice" | "syncSalePrice" | "delete";
  product: IProductSummary;
}

@Component({
  selector: "app-product-expanded-row",
  standalone: true,
  imports: [CommonModule, ButtonModule, TagModule, TooltipModule],
  templateUrl: "./product-expanded-row.component.html",
  styleUrl: "./product-expanded-row.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductExpandedRowComponent {
  @Input() product!: IProductSummary;
  @Input() loading = false;

  @Output() rowAction = new EventEmitter<IProductExpandedRowAction>();

  /**
   * Format price for display
   */
  formatPrice(price: number | undefined | null): string {
    if (price === null || price === undefined) {
      return "N/A";
    }
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(price);
  }

  /**
   * Format availability text for readable display
   * Converts "AddToScheduledOrder, ProcessScheduledOrder" to readable format
   */
  formatAvailability(availability: string | undefined | null): string[] {
    if (!availability) return ["N/A"];

    // Split by comma and format each part
    return availability.split(",").map((item) => {
      const trimmed = item.trim();
      // Add spaces before capital letters: "AddToScheduledOrder" -> "Add To Scheduled Order"
      return trimmed.replace(/([A-Z])/g, " $1").trim();
    });
  }

  /**
   * Get stock level severity - matches table logic (only checks StockLevel string)
   */
  getStockSeverity(): "success" | "warn" | "danger" | "secondary" {
    if (!this.product) return "secondary";

    const stockLevel = this.product.StockLevel?.toLowerCase() ?? "";

    if (stockLevel.includes("outofstock") || stockLevel.includes("out")) {
      return "danger";
    }
    if (stockLevel.includes("low") || stockLevel.includes("backorder")) {
      return "warn";
    }
    if (stockLevel.includes("instock") || stockLevel.includes("in")) {
      return "success";
    }
    return "secondary";
  }

  /**
   * Get stock text for display
   */
  getStockText(): string {
    if (!this.product) return "N/A";

    const stockLevel = this.product.StockLevel;
    if (stockLevel === "InStock") return "In Stock";
    if (stockLevel === "LowStock") return "Low Stock";
    if (stockLevel === "OutOfStock") return "Out of Stock";
    return stockLevel || "Unknown";
  }

  /**
   * Check if product has scheduled order stats
   */
  hasScheduledStats(): boolean {
    if (!this.product) return false;
    return !!(
      this.product.TotalQuantityScheduledActive ||
      this.product.TotalQuantityScheduledPaused ||
      this.product.TotalQuantityFailed
    );
  }

  // Action handlers
  onView(): void {
    this.rowAction.emit({ action: "view", product: this.product });
  }

  onEdit(): void {
    this.rowAction.emit({ action: "edit", product: this.product });
  }

  onSyncPrice(): void {
    this.rowAction.emit({ action: "syncPrice", product: this.product });
  }

  onSyncSalePrice(): void {
    this.rowAction.emit({ action: "syncSalePrice", product: this.product });
  }

  onDelete(): void {
    this.rowAction.emit({ action: "delete", product: this.product });
  }
}
